import { useState, useEffect } from "react";
import { CheckCircle2, Mail, Send, Loader2, AlertTriangle, Circle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";

const Step = ({ n, title, done, children }) => (
  <div className="flex gap-4" data-testid={`email-setup-step-${n}`}>
    <div className="flex flex-col items-center">
      {done ? <CheckCircle2 className="w-7 h-7 text-green-600" /> : <div className="w-7 h-7 rounded-full border-2 border-slate-300 text-slate-500 text-sm font-bold flex items-center justify-center">{n}</div>}
      <div className="w-px flex-1 bg-slate-200 mt-2" />
    </div>
    <div className="flex-1 pb-8">
      <p className="font-semibold text-slate-900 mb-2">{title}</p>
      {children}
    </div>
  </div>
);

export const EmailSetupTab = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ from_name: "", reply_to: "", enabled: true });
  const [busy, setBusy] = useState("");

  const load = () => api.get("/api/admin/email-settings").then(r => { setS(r.data); setForm({ from_name: r.data.from_name, reply_to: r.data.reply_to, enabled: r.data.enabled }); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put("/api/admin/email-settings", form); setS(data); toast.success("Email settings saved"); }
    catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Save failed"); }
    finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/api/admin/email-settings/test"); toast.success(`Test email sent to ${data.to}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Test failed"); load(); }
    finally { setBusy(""); }
  };

  if (!s) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const saved = !!s.from_name && s.from_name === form.from_name && s.reply_to === form.reply_to;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-3xl" data-testid="email-setup-tab">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Email Setup</h2>
          <p className="text-sm text-slate-500">Emails are delivered through Resend, managed for you. No Resend account or API key is needed.</p>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.configured && s.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`} data-testid="email-setup-status">{s.configured ? (s.enabled ? "Sending on" : "Paused") : "Not connected"}</span>
      </div>

      <Step n={1} title="Email service connected" done={s.configured}>
        <p className="text-sm text-slate-600">{s.configured ? "Your app is connected to managed Resend delivery." : "Email delivery isn't connected for this app yet."}</p>
      </Step>

      <Step n={2} title="Sender name & reply-to inbox" done={saved}>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div><Label className="text-sm">Sender name (your business)</Label><Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} data-testid="email-setup-from-name" /></div>
          <div><Label className="text-sm">Reply-to inbox</Label><Input type="email" placeholder="you@yourshop.com" value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} data-testid="email-setup-reply-to" /></div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Replies go to this inbox, and the test email is sent here. The from address itself is managed for you.</p>
        <button onClick={save} disabled={busy === "save"} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5" data-testid="email-setup-save-btn">{busy === "save" && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
      </Step>

      <Step n={3} title="Send a test email" done={!!s.last_test?.ok}>
        {s.last_test?.ok && <p className="text-sm text-green-700 mb-2" data-testid="email-setup-last-test">Last test delivered to {s.last_test.to} on {s.last_test.at.slice(0, 10)}</p>}
        {s.last_test && !s.last_test.ok && <p className="text-sm text-red-600 mb-2 flex items-start gap-1.5" data-testid="email-setup-last-test-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />Last test failed: {s.last_test.error}</p>}
        {s.last_error && <p className="text-sm text-red-600 mb-2 flex items-start gap-1.5" data-testid="email-setup-last-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{s.last_error.message}</p>}
        <button onClick={test} disabled={busy === "test" || !s.configured || !saved} className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5" data-testid="email-setup-test-btn">
          {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send test to {s.reply_to || "your admin email"}
        </button>
        {!saved && <p className="text-xs text-slate-400 mt-1.5">Save step 2 first.</p>}
      </Step>

      <Step n={4} title="Turn sending on" done={s.enabled && !!s.last_test?.ok}>
        <div className="flex items-center gap-3">
          <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="email-setup-enabled" />
          <span className="text-sm text-slate-600">{form.enabled ? "Proposals, alerts and notifications will be emailed" : "All outgoing email is paused"}</span>
        </div>
        {form.enabled !== s.enabled && <button onClick={save} className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-500" data-testid="email-setup-save-enabled">Save change</button>}
        <ul className="mt-4 space-y-1 text-xs text-slate-500">
          {["Proposal emails to prospects", "\"Prospect is viewing\" and \"accepted\" alerts to reps", "Invoices, meeting invites and account updates"].map(t => <li key={t} className="flex items-center gap-1.5"><Circle className="w-1.5 h-1.5 fill-current" />{t}</li>)}
        </ul>
      </Step>
    </div>
  );
};
